import { useState } from "react";
import {
  Button,
  InlineStack,
  Box,
  Text,
  Icon,
  BlockStack,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  EditIcon,
  DuplicateIcon,
  DeleteIcon,
  XSmallIcon,
  ExternalSmallIcon
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { EventTicket } from "@features/events/types";
import { getLocalizedText } from "@shared/lib";
import { URLLinkBuilder } from "@shared/lib";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { formatDate } from "@shared/lib";
import { Currency } from "@shared/ui";
import { useTaxCalculator } from "@shared/hooks/common/useTaxCalculator";

export interface TicketItemProps {
  ticket: EventTicket;
  eventId: string;
  onEdit: (ticket: EventTicket) => void;
  onDuplicate: (ticket: EventTicket) => void;
  onDelete: (ticketId: string) => void;
  isDeleting?: boolean;
  isReordering?: boolean;
}

export default function TicketItem({
  ticket,
  eventId,
  onEdit,
  onDuplicate,
  onDelete,
  isDeleting = false,
  isReordering = false,
}: TicketItemProps) {
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const { metadata } = useEventMetadata();
  const { calculateTax, taxRate } = useTaxCalculator();

  // Calculate total price with tax (price is VAT exclusive)
  const taxCalculation = calculateTax(ticket.price ?? 0, ticket.currency, false);

  const getTicketSlug = (slug: string) => {
    return (
      metadata?.ticketStatus.find((item) => item.slug === slug)?.slug ?? ""
    );
  };

  const slug = getTicketSlug(ticket.status);
  const isArchived = slug === "archived";
  const isUnlisted = slug === "unlisted";

  return (
    <SortableResourceItem
      accessibilityLabel={`Edit ${getLocalizedText(ticket.title) || "ticket"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(ticket.title) || "ticket"}`}
    >
      <div
        onMouseEnter={() => !isReordering && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box opacity={isArchived ? "0.5" : "1"}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto auto auto",
              gap: "var(--p-space-400)",
              alignItems: "center",
            }}
          >
            <InlineStack gap="100" blockAlign="center" wrap={false}>
              <Box minWidth="0" width="100%">
                <BlockStack gap="100">
                  <Text variant="bodyMd" as="span" truncate>
                    {getLocalizedText(ticket.title) || "Untitled"}
                  </Text>
                  {ticket.expiresAt && (
                    <Text variant="bodyMd" as="span" tone="subdued">
                      {t("eventDetails.tickets.expiresDate", {
                        date: formatDate(ticket.expiresAt),
                      })}
                    </Text>
                  )}
                </BlockStack>
              </Box>
            </InlineStack>

            <EventBadgeRenderer
              slug={getTicketSlug(ticket.status) ?? ""}
              entity="ticket"
            />

            <Box minWidth="80px">
              <BlockStack gap="050">
                <Text variant="bodyMd" as="span" alignment="end">
                  <Currency value={ticket.price ?? 0} currency={ticket.currency} />
                </Text>
                <Text variant="bodySm" as="span" alignment="end" tone="subdued">
                  {t("eventDetails.tickets.withTax", {
                    price: taxCalculation.totalPriceLabel,
                    tax: taxRate,
                  })}
                </Text>
              </BlockStack>
            </Box>

            <InlineStack gap="0" blockAlign="center" align="center" wrap={false}>
              <Box padding="025">
                <Icon source={XSmallIcon} />
              </Box>
              <Text variant="bodyMd" as="span">
                {ticket.minQuantity}
              </Text>
            </InlineStack>

            <Box
              minWidth="100px"
              opacity={isHovered && !isReordering ? "1" : "0"}
            >
              <InlineStack
                gap="200"
                blockAlign="center"
                align="end"
                wrap={false}
              >
                {isUnlisted && eventId && (
                  <Button
                    icon={ExternalSmallIcon}
                    size="micro"
                    variant="tertiary"
                    url={URLLinkBuilder.checkoutTicketPromo(eventId, ticket.id, i18n.language)}
                    external
                    disabled={isDeleting || isReordering}
                  />
                )}
                <Button
                  icon={EditIcon}
                  size="micro"
                  variant="tertiary"
                  onClick={() => onEdit(ticket)}
                  disabled={isDeleting || isReordering}
                />
                <Button
                  icon={DuplicateIcon}
                  size="micro"
                  variant="tertiary"
                  onClick={() => onDuplicate(ticket)}
                  disabled={isDeleting || isReordering}
                />
                <Button
                  icon={DeleteIcon}
                  size="micro"
                  variant="tertiary"
                  tone="critical"
                  onClick={() => onDelete(ticket.id)}
                  loading={isDeleting}
                  disabled={isReordering}
                />
              </InlineStack>
            </Box>
          </div>
        </Box>
      </div>
    </SortableResourceItem>
  );
}
